import * as React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Tabs } from "@jhool-io/fe-components";
import BasicInfo from "../../../../components/Clients/Client/BasicInfo/BasicInfo";
import PersonalInfo from "../../../../components/Clients/Client/PersonalInfo/PersonalInfo";
import Navbar from "../../../../components/Navbar/Navbar";
import { useFetchUserDetails } from "../../../../hooks/queries/user";
import { UserPermisions, UserRole } from "../../../../utils/types/user";
import {
    useDoesUserHavePermission,
    useDoesUserHaveRole,
} from "../../../../hooks/permissions";
import { useFetchTasks } from "../../../../hooks/queries/tasks";
import { TaskStatus } from "../../../../utils/types/tasks";
import DocumentsList from "../../../../modules/clients/components/DocumentsList/DocumentsList";
import Insurance from "../../../../modules/clients/components/Insurance/Insurance";
import ClientNotesList from "../../../../modules/clients/components/ClientNotesList/ClientNotesList";
import ClientMemoList from "../../../../modules/clients/components/ClientMemoList/ClientMemoList";
import ClientCollectionsNotes from "../../../../modules/clients/components/ClientCollectionsNotes/ClientCollectionsNotes";
import ClientClaimsList from "../../../../modules/clients/components/ClientClaimsList/ClientClaimsList";
import { TaskListSortAttribute } from "../../../../modules/tasks/types/tasks.types";
import ClientTasksList from "../../../../modules/clients/components/ClientTasksList/ClientTasksList";
import { AppSortOrder } from "../../../../utils/types";
import { CLIENT_CHART_TASK_CATEGORIES } from "../../../../utils/constants";
import {
    getClientClaimsListNavigateUrl,
    getMemoListNavigateUrl,
} from "../../../../modules/clients/helpers/clients.helpers";
import { useAppSelector } from "../../../../hooks/useRedux";
import ClientProvidersHistoryList from "../../../../modules/clients/components/ClientProvidersHistoryList/ClientProvidersHistoryList";
import ClientCommunications from "../../../../modules/clients/components/ClientCommunications/ClientCommunications";
import ClientIntakeImportDiagnosisInfo from "../../../../modules/clients/components/ClientIntakeImportDiagnosisInfo/ClientIntakeImportDiagnosisInfo";
import { useFetchClientBasicAndPersonalInfo } from "../../../../hooks/queries/client";
import ClientMedicationList from "../../../../modules/e-prescirbe/components/ClientMedicationList/ClientMedicationList";
import ClientAllergyList from "../../../../modules/e-prescirbe/components/ClientAllergyList/ClientAllergyList";
import useCanViewEPrescribeFlow from "../../../../modules/e-prescirbe/hooks/useCanViewEPrescribeFlow";
import ClientPrescriptionsViewWrapper from "../../../../modules/e-prescirbe/components/ClientPrescriptionsViewWrapper/ClientPrescriptionsViewWrapper";
import LabRequestList from "../../../../modules/e-prescirbe/components/LabRequestList/LabRequestList";

export default function Client() {
    const [searchParams, setSearchParams] = useSearchParams();

    const loggedInUser = useFetchUserDetails();

    const { practice } = useAppSelector((state) => state.userPractice);
    const canViewEPrescribeFlow = useCanViewEPrescribeFlow();

    const params = useParams();
    const { clientId } = params;

    const clientData = useFetchClientBasicAndPersonalInfo(
        clientId || "",
        Boolean(clientId)
    );

    const tabInUrl = searchParams.get("tab");

    const taskTabSubTabInUrl = searchParams.get("task_subtab");

    const canViewIntakeImportDiagnosis =
        (import.meta.env.VITE_NODE_ENV === "production" &&
            loggedInUser?.data?.user_id ===
                "b38cf5fa-f83d-4ac2-b0af-ab5f34dd9d02") ||
        import.meta.env.VITE_NODE_ENV !== "production";

    const { data, isLoading, error } = useFetchTasks({
        client_id: clientId || "",
        assignee_user_id: loggedInUser.data?.user_id || "",
        creator_user_id: loggedInUser.data?.user_id || "",
        user_id_query_logic: "OR",
        status:
            taskTabSubTabInUrl === "all_tasks"
                ? [
                      TaskStatus.COMPLETED,
                      TaskStatus.IN_PROGRESS,
                      TaskStatus.NOT_STARTED,
                  ]
                : [TaskStatus.IN_PROGRESS, TaskStatus.NOT_STARTED],
        category: CLIENT_CHART_TASK_CATEGORIES,
        sort_attribute: TaskListSortAttribute.DUE_DATE,
        sort_order: AppSortOrder.ASC,
    });

    let taskCount;
    if (isLoading) taskCount = 0;
    if (error) taskCount = 0;
    if (data) taskCount = data.total_count;

    const canSeePaymentTab = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_ALL_ADMIN,
        UserPermisions.USER_INFO_ALL_ADMIN,
        UserPermisions.CLIENT_INFO_INVOICE_READ,
        UserPermisions.CLIENT_INFO_PAYMENTS_READ,
    ]);

    const userIsProviderOrAuditor =
        practice?.roles?.includes(UserRole.PROVIDER) ||
        practice?.roles?.includes(UserRole.AUDITOR);

    const userCanViewPersonalInfoTab = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_ALL_ADMIN,
        UserPermisions.CLIENT_INFO_PROVIDER_READ,
        UserPermisions.CLIENT_INFO_PERSONAL_WRITE,
        UserPermisions.CLIENT_INFO_ALL_WRITE,
        UserPermisions.CLIENT_INFO_PROVIDER_WRITE,
    ]);

    const userCanViewCommunications = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_ALL_ADMIN,
        UserPermisions.CLIENT_INFO_COMMUNICATIONS_READ,
    ]);

    const userCanViewSessionsHistory = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_ALL_ADMIN,
        UserPermisions.CLIENT_INFO_PROVIDER_READ,
        UserPermisions.CLIENT_INFO_PROVIDER_WRITE,
    ]);

    const userCanViewCollectionsNotes = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_ALL_ADMIN,
        UserPermisions.CLIENT_INFO_COLLECTIONS_NOTES_READ,
        UserPermisions.CLIENT_INFO_COLLECTIONS_NOTES_WRITE,
    ]);

    const handleTabInView = (tab: string) => {
        if (tab === "claims") {
            return setSearchParams(getClientClaimsListNavigateUrl());
        }
        if (tab === "memo") {
            const memoParams = getMemoListNavigateUrl();
            return setSearchParams(`${memoParams}`);
        }
        return setSearchParams(`tab=${tab}`);
    };

    const tabItems = [
        {
            label: "Memo",
            key: "memo",
            children: <ClientMemoList />,
            onClick: () => handleTabInView("memo"),
        },
        {
            label: "Collections notes",
            key: "collections-notes",
            children: <ClientCollectionsNotes />,
            hide: !userCanViewCollectionsNotes,
            onClick: () => handleTabInView("collections-notes"),
        },
        {
            label: "Sessions history",
            key: "sessions-history",
            children: <ClientProvidersHistoryList />,
            hide: !userCanViewSessionsHistory,
            onClick: () => handleTabInView("sessions-history"),
        },
        {
            label: "Relations info",
            key: "relations-info",
            children: <PersonalInfo />,
            hide: !userCanViewPersonalInfoTab,
            onClick: () => handleTabInView("relations-info"),
        },
        {
            label: "Insurance/Fees",
            key: "insurance",
            children: <Insurance />,
            hide: userIsProviderOrAuditor,
            onClick: () => handleTabInView("insurance"),
        },
        {
            label: "Claims",
            key: "claims",
            children: <ClientClaimsList />,
            hide:
                !canSeePaymentTab ||
                practice?.roles?.includes(UserRole.AUDITOR) ||
                practice?.roles?.includes(UserRole.AUDITOR_ADMIN),
            onClick: () => handleTabInView("claims"),
        },
        {
            label: "Session Notes",
            key: "session_notes",
            children: <ClientNotesList />,
            onClick: () => handleTabInView("session_notes"),
        },
        {
            label: "Allergies",
            key: "allergies",
            children: <ClientAllergyList />,
            onClick: () => handleTabInView("allergies"),
            hide: !canViewEPrescribeFlow,
        },
        {
            label: "Medications",
            key: "medications",
            children: <ClientMedicationList />,
            onClick: () => handleTabInView("medications"),
            hide: !canViewEPrescribeFlow,
        },
        {
            label: "Prescriptions",
            key: "prescriptions",
            children: <ClientPrescriptionsViewWrapper />,
            onClick: () => handleTabInView("prescriptions"),
            hide: !canViewEPrescribeFlow,
        },
        {
            label: "Lab Requests",
            key: "lab_requests",
            children: <LabRequestList />,
            onClick: () => handleTabInView("lab_requests"),
            hide: !canViewEPrescribeFlow,
        },
        {
            label: "Communications",
            key: "communications",
            children: <ClientCommunications />,
            hide: !userCanViewCommunications,
            onClick: () => handleTabInView("communications"),
        },
        {
            label: "Documents",
            key: "documents",
            children: <DocumentsList />,
            hide:
                practice?.roles?.includes(UserRole.AUDITOR) &&
                !practice?.roles?.includes(UserRole.PROVIDER),
            onClick: () => handleTabInView("documents"),
        },
        {
            label: "Tasks",
            key: "tasks",
            children: <ClientTasksList />,
            count: taskCount,
            onClick: () => handleTabInView("tasks"),
        },
    ];

    const roleDefaultKey =
        !useDoesUserHaveRole([
            UserRole.BILLER,
            UserRole.BILLER_ADMIN,
            UserRole.CLIENT_SUPPORT,
        ]) && userCanViewSessionsHistory
            ? "sessions-history"
            : tabItems.filter((item) => !item?.hide)[0].key;
    return (
        <>
            <Navbar title="Client" />
            <div className="page">
                <BasicInfo />
                {canViewIntakeImportDiagnosis && clientData.isSuccess ? (
                    <div className="mt-[--spacing-header]">
                        <ClientIntakeImportDiagnosisInfo
                            clientId={
                                clientData.data.data.intake_client_id || ""
                            }
                        />
                    </div>
                ) : null}
                <Tabs
                    items={tabItems.filter((item) => !item?.hide)}
                    size="medium"
                    className="w-full rounded-none mt-[--spacing-sections]"
                    defaultActiveKey={tabInUrl || roleDefaultKey}
                />
            </div>
        </>
    );
}
